import { getSupabaseClient, type DdmSupabaseClient } from '../supabase';

const BUCKET = 'seller-photos';
const DEFAULT_TTL_SECONDS = 3600;

/**
 * The `seller-photos` bucket is PRIVATE (20260508120000_seller_submissions.sql
 * lines 647-655). `getPublicUrl()` returns a non-functional URL — every
 * client read MUST go through `createSignedUrl()`, which the
 * `seller_photos_storage_select` policy gates to admin / super_admin.
 *
 * Returns a one-hour signed URL by default. PostgrestError with code
 * `42501` (RLS denied) propagates to the caller; missing files surface
 * later when the browser <img> tag fetches the URL — the helper does not
 * pre-validate object existence.
 */
export async function getSellerPhotoSignedUrl(
  storageKey: string,
  ttlSeconds: number = DEFAULT_TTL_SECONDS,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<string> {
  const { data, error } = await client.storage.from(BUCKET).createSignedUrl(storageKey, ttlSeconds);

  if (error) throw error;
  if (!data?.signedUrl) {
    throw new Error(`createSignedUrl returned no signedUrl for ${storageKey}`);
  }
  return data.signedUrl;
}

export type SellerPhotoSignedEntry = {
  id: string;
  position: number;
  storage_key: string;
  signedUrl: string;
};

export type PhotoLike = Pick<SellerPhotoSignedEntry, 'id' | 'position' | 'storage_key'>;

/**
 * Batch helper: fetch signed URLs for every photo of a submission while
 * preserving `position` ordering. Internally `Promise.all`, so the caller
 * sees the slowest signed URL latency, not the sum. An empty input array
 * returns `[]` without hitting the network.
 */
export async function getSellerSubmissionPhotoUrls(
  photos: ReadonlyArray<PhotoLike>,
  ttlSeconds: number = DEFAULT_TTL_SECONDS,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<SellerPhotoSignedEntry[]> {
  if (photos.length === 0) return [];

  const sorted = [...photos].sort((a, b) => a.position - b.position);
  const signedUrls = await Promise.all(
    sorted.map((p) => getSellerPhotoSignedUrl(p.storage_key, ttlSeconds, client)),
  );

  return sorted.map((p, i) => ({
    id: p.id,
    position: p.position,
    storage_key: p.storage_key,
    signedUrl: signedUrls[i]!,
  }));
}
